import { svgToIcon } from '../utils/svg';
import PolygonBase from './PolygonBase';
import circleArrowIcon from '../assets/icons/shapeCircleArrow.svg';
import { type IPoint } from 'fabric/fabric-impl';
import type Shape from '../objects/Shape';

function rotate(p: IPoint, a: number): IPoint {
  const { cos, sin } = Math;
  const ca = cos(a);
  const sa = sin(a);

  const x = p.x * ca - p.y * sa;
  const y = p.x * sa + p.y * ca;

  return { x, y };
}

class ShapeCircleArrow extends PolygonBase {
  constructor(fabricObject: Shape) {
    super(fabricObject);
    this.fabricObject.uniformScaling = true;
  }

  static pathString() {
    const tailThickness = 1 / 12;
    const headThickness = 1 / 6;

    const { cos, sin } = Math;
    const w = this.width;
    const h = this.height;
    const angle = Math.PI / 3;
    const ratio = w / h;
    const transformPoint = (pp: IPoint, scale: number = 1): IPoint => {
      const p = rotate(pp, angle);
      return ratio > 1
        ? // Wide
          { x: (p.x * scale) / ratio, y: p.y * scale }
        : // Narrow
          { x: p.x * scale, y: p.y * scale * ratio };
    };

    const tailScale = 0.5 - tailThickness;
    const innerScale = tailScale - tailThickness;
    const outerScale = tailScale + tailThickness;

    const hp = Math.PI / 2;
    const curve = [
      { x: cos(hp * -0.5), y: sin(hp * -0.5) },
      { x: cos(hp * 0), y: sin(hp * 0) },
      { x: cos(hp * 1), y: sin(hp * 1) },
      { x: cos(hp * 2), y: sin(hp * 2) },
      { x: cos(hp * 3), y: sin(hp * 3) },
    ];

    const headPos = { x: 0.0, y: -0.5 + headThickness / 2 };
    const headPoints = [
      // Bottom point of arrow
      { x: headPos.x, y: headPos.y - headThickness },

      // Tip
      { x: headPos.x + headThickness, y: headPos.y },

      // Top point of arrow
      { x: headPos.x, y: headPos.y + headThickness },
    ];

    let path = '';

    // Outer edge of tail
    for (let i = 0; i < curve.length - 1; i++) {
      const p0 = curve[i];
      const p1 = curve[i + 1];
      const [p2, p3] = arcToBezier(p0, p1);

      const sp0 = transformPoint(p0, outerScale);
      const sp1 = transformPoint(p1, outerScale);
      const sp2 = transformPoint(p2, outerScale);
      const sp3 = transformPoint(p3, outerScale);

      if (i === 0) {
        // End cap
        path += `M ${sp0.x} ${sp0.y}\n`;
      }
      path += `C ${sp2.x} ${sp2.y}, ${sp3.x} ${sp3.y}, ${sp1.x} ${sp1.y}\n`;
    }

    // Arrow head
    for (const p of headPoints) {
      const { x, y } = transformPoint(p);
      path += `L ${x} ${y}\n`;
    }

    // Inner edge of tail
    for (let i = curve.length - 1; i > 0; i--) {
      const p0 = curve[i];
      const p1 = curve[i - 1];
      const [p2, p3] = arcToBezier(p0, p1);

      const sp0 = transformPoint(p0, innerScale);
      const sp1 = transformPoint(p1, innerScale);
      const sp2 = transformPoint(p2, innerScale);
      const sp3 = transformPoint(p3, innerScale);

      if (i === curve.length - 1) {
        path += `L ${sp0.x} ${sp0.y}\n`;
      }
      path += `C ${sp2.x} ${sp2.y}, ${sp3.x} ${sp3.y}, ${sp1.x} ${sp1.y}\n`;
    }

    path += 'Z';

    return path;
  }

  static renderIcon() {
    return svgToIcon(circleArrowIcon);
  }
}

// Calculate cubic bezier control points for arc from p0 to p3
function arcToBezier(p0: IPoint, p1: IPoint): [IPoint, IPoint] {
  // angle between points
  const theta = Math.acos(p0.x * p1.x + p0.y * p1.y); // dot product
  const cross = p0.x * p1.y - p0.y * p1.x; // determine direction
  const dir = cross < 0 ? -1 : 1;

  const t = (4 / 3) * Math.tan(theta / 4); // arc magic number

  // rotate p0 90° CCW to get its tangent
  const dx = -p0.y * t * dir;
  const dy = p0.x * t * dir;
  const p2 = { x: p0.x + dx, y: p0.y + dy };

  // rotate p1 90° CW to get its tangent
  const dx3 = p1.y * t * dir;
  const dy3 = -p1.x * t * dir;
  const p3 = { x: p1.x + dx3, y: p1.y + dy3 };

  return [p2, p3];
}

export default ShapeCircleArrow;
